import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { defaultAlert } from "../../utils/alert";
import { getMemberAuthTermsList } from "../../api/termsApi";
import { getPassAuthResult, getPassAuthUrl, generateRequestNo } from "../../api/passApi";
import { TermsTypes } from "../../types/terms.types";
import TermsModal from "../../components/common/TermsModal";

const SignupTermsPage = () => {
    const navigate = useNavigate();

    //서버에서 가져온 약관 내역
    const [termsList, setTermsList] = useState<TermsTypes[]>([]);
    //동의한 약관 리스트
    const [agreedTerms, setAgreedTerms] = useState<{ termsCode: string; termsGroupCode: string; termsYn: boolean }[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);

    const [openModal, setOpenModal] = useState<{ termsName: string, contents: string, isOpen: boolean; }>(
        {
            termsName: '',
            contents: '',
            isOpen: false
        }
    );

    const handleOpenTermsModal = (termsName: string, contents: string) => {
        setOpenModal({
            termsName,
            contents,
            isOpen: true
        });
    };

    const handleCloseTermsModal = () => {
        setOpenModal(prev => ({
            ...prev,
            isOpen: false
        }));
    };

    const getTermsList = async () => {
        try {
            const result = await getMemberAuthTermsList();
            if (result.data.code == "00") {
                setTermsList(result.data.data);
                const initialAgreedTerms = result.data.data.map((item: TermsTypes) => ({
                    termsCode: item.termsCode,
                    termsGroupCode: item.termsGroupCode,
                    termsYn: false
                }));
                setAgreedTerms(initialAgreedTerms);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleTermCheck = (term: string) => {
        setAgreedTerms(prev =>
            prev.map(item =>
                item.termsCode === term
                    ? {...item, termsYn: !item.termsYn}
                    : item
            )
        );
    };

    const handleAllCheck = (checked: boolean) => {
        setAgreedTerms(prev =>
            prev.map(item => ({...item, termsYn: checked}))
        );
    };

    const handleVerify = async () => {
        if (!termsAgreeValidation()) {
            defaultAlert('필수 약관에 모두 동의해 주세요.');
            return;
        }

        // 약관 동의 내역 sessionStorage에 저장
        sessionStorage.setItem('agreedTerms', JSON.stringify(agreedTerms));

        try {
            const requestNo = generateRequestNo();
            sessionStorage.setItem('passRequestNo', requestNo);
            const closeUrl = `${window.location.origin}/signup/terms`;
            const response = await getPassAuthUrl(requestNo, closeUrl);
            window.location.href = response.authUrl;
        } catch (err) {
            console.error('[NICE] 인증 URL 발급 실패:', err);
            defaultAlert('본인인증 URL 발급에 실패했습니다. 다시 시도해 주세요.');
        }
    };

    //약관 동의 체크
    const termsAgreeValidation = () => {
        const requiredTerms = termsList.filter(item => item.essential);

        const allRequiredAgreed = requiredTerms.every(requiredTerm => {
            const agreed = agreedTerms.find(a => a.termsCode === requiredTerm.termsCode);
            return agreed?.termsYn === true;
        });
        return allRequiredAgreed;
    }

    // NICE 인증 후 콜백 처리
    useEffect(() => {
        const handlePassCallback = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const txId = urlParams.get('txId');
            const error = urlParams.get('error');

            // 인증 실패 처리
            if (error) {
                window.history.replaceState({}, '', window.location.pathname);
                defaultAlert('본인인증에 실패했습니다. 다시 시도해 주세요.');
                return;
            }

            if (txId && !isProcessing) {
                setIsProcessing(true);

                const requestNo = sessionStorage.getItem('passRequestNo');
                if (!requestNo) {
                    defaultAlert('인증 세션이 만료되었습니다. 다시 시도해 주세요.');
                    window.history.replaceState({}, '', window.location.pathname);
                    setIsProcessing(false);
                    return;
                }

                try {
                    // NICE 인증 결과 조회 (txId + requestNo)
                    const passResult = await getPassAuthResult(txId, requestNo);

                    // 커스텀 리턴 경로가 있는 경우 (예: 휴대폰번호 변경)
                    const niceReturnPath = sessionStorage.getItem('niceReturnPath');
                    if (niceReturnPath) {
                        sessionStorage.removeItem('niceReturnPath');
                        sessionStorage.removeItem('passRequestNo');
                        navigate(niceReturnPath, {
                            state: { passVerifyData: passResult },
                            replace: true
                        });
                        return;
                    }

                    // 저장했던 약관 동의 내역 불러오기
                    const savedTerms = sessionStorage.getItem('agreedTerms');
                    const agreedTermsList = savedTerms ? JSON.parse(savedTerms) : [];

                    // sessionStorage 정리
                    sessionStorage.removeItem('agreedTerms');
                    sessionStorage.removeItem('passRequestNo');

                    // 다음 페이지(회원가입 정보 입력)로 이동
                    navigate('/signup/write', {
                        state: {
                            agreedTerms: agreedTermsList,
                            passVerifyData: passResult
                        },
                        replace: true
                    });
                } catch (error) {
                    console.error('[PASS 콜백] 인증 결과 조회 실패:', error);
                    defaultAlert('본인인증 처리 중 오류가 발생했습니다.');
                    sessionStorage.removeItem('niceReturnPath');
                    window.history.replaceState({}, '', window.location.pathname);
                    setIsProcessing(false);
                }
            }
        };

        getTermsList();
        handlePassCallback();
    }, []);

    const isAllChecked = agreedTerms.length > 0 && agreedTerms.every(item => item.termsYn);

    // 뒤로가기: /signup/write가 React Router 히스토리에 남아있을 수 있으므로
    // window.history.state의 idx로 뒤로 갈 수 있는 실제 위치를 판단
    // idx === 0이면 히스토리 첫 항목 → navigate(-1) 대신 '/'로 이동
    const handleBack = () => {
        const idx = (window.history.state as any)?.idx;
        if (idx === undefined || idx <= 0) {
            navigate('/', { replace: true });
        } else {
            navigate(-1);
        }
    };

    return (
        <>
            <header className="sub-header">
                <div className="header-inner">
                    <button type="button" className="backBtn" onClick={handleBack}>
                        <span className="blind">뒤로가기</span>
                    </button>
                </div>
            </header>

            <div className="bottom-btn">
                <button type="button" className="btn cancel" onClick={handleBack}>이전</button>
                <button
                    type="button"
                    className="btn confirm"
                    onClick={handleVerify}
                    disabled={isProcessing}
                >
                    {isProcessing ? '처리중...' : '본인인증'}
                </button>
            </div>

            <main className="container">
                <section className="tit-box-wrap">
                    <h2 className="headline">약관동의</h2>
                    <p className="subtit">회원 가입을 위하여 약관에 동의해 주세요.</p>
                    <ol className="join-process">
                        <li className="on"><span>1</span></li>
                        <li><span>2</span></li>
                    </ol>
                </section>

                <section>
                    <div className="inner">
                        <div className="all-terms-check">
                            <span className="check_box">
                                 <input
                                     id="terms_all"
                                     type="checkbox"
                                     checked={isAllChecked}
                                     onChange={(e) => handleAllCheck(e.target.checked)}
                                 />
                                <label htmlFor="terms_all" className="inline-block">
                                    전체 동의합니다.
                                </label>
                            </span>
                        </div>

                        {termsList?.map((item: TermsTypes, index: number) => {
                            const agreed = agreedTerms.find(a => a.termsCode === item.termsCode);
                            return (
                                <div className="default-checkbox" key={index}>
                                    <input
                                        id={item.termsGroupCode}
                                        type="checkbox"
                                        value={item.termsCode}
                                        checked={agreed?.termsYn || false}
                                        onChange={() => handleTermCheck(item.termsCode)}
                                    />
                                    <label htmlFor={item.termsGroupCode} className="right-arrow">
                                        {item.essential ? "[필수]" : "[선택]"} {item.termsName}
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => handleOpenTermsModal(item.termsName, item.contents)}
                                    ><i className="arrow right"></i></button>
                                </div>
                            );
                        })}
                    </div>
                </section>
            </main>

            {openModal.isOpen && (
                <TermsModal
                    termsName={openModal.termsName}
                    contents={openModal.contents}
                    onClose={handleCloseTermsModal}
                />
            )}
        </>
    );
};

export default SignupTermsPage;